import { readEntitiesByIDs, watchEntitiesByIDs, readAllEntities, watchAllEntities } from '../server/entity-reads.js';
import { A } from '@adornis/base/env-info.js';
import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { Int, ID } from '@adornis/baseql/baseqlTypes.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { registerMutation, registerSubscription, registerQuery } from '@adornis/baseql/metadata/register.js';
import { context, serverContext } from '@adornis/baseql/server/context.js';
import { loadMongoData } from '@adornis/baseql/server/data-loader.js';
import { getContextFromRequest } from '@adornis/baseql/server/getContext.js';
import { runInContext } from '@adornis/baseql/server/server.js';
import { watchEntireCollection } from '@adornis/baseql/server/watchQuery.js';
import { type BaseQLSelectionSet, selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { checkAuthorizedMiddleware } from '@adornis/bh-util/middlewares/checkAuthorizedMiddleware.js';
import { getCurrentUserServerOrFail } from '@adornis/bh-util/util.js';
import { AdornisFile } from '@adornis/file-utils/db/files.js';
import { AdornisTemplater } from '@adornis/templater/server/index.js';
import { format } from 'date-fns';
import { Router } from 'express';
import { concatMap, from, map, of, switchMap, tap } from 'rxjs';
import { BHFilter } from '../entities/bh-filter.js';
import { CertificateWorkshopSet, WORKSHOP_SET_CERTIFICATE_VARIABLES } from '../entities/certificate-workshop-set.js';
import { ParticipationWorkshop } from '../entities/ParticipationWorkshop.js';
import {
  buildifyHtmlToReadableText,
  CertificateAttachedProduct,
  Product,
  ProductFilter,
} from '../entities/products.js';
import { TextMultipleLangItem } from '../entities/TextMultipleLangItem.js';
import { WorkshopSet } from '../entities/WorkshopSet.js';
import { Workshop, WorkshopAffiliation } from '../entities/workshop.js';
import { ParticipationState } from '../enums/enums.js';
import { Permission } from '../enums/roles.js';
import { notifyWorkshopSetCompletion } from '../notification/workshop-set-completion.js';
import { mongo } from '../server/mongo.js';
import { withIdTiebreaker } from '../util.js';
import { getCertificateWorkshopSetByID } from './certificate-workshop-set.js';
import { getAdornisFileByID } from './files.js';

export const subscribeWorkshopSetPaginated = registerSubscription({
  operationName: 'subscribeWorkshopSetPaginated',
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'filter', type: () => BHFilter },
  ],
  type: () => [WorkshopSet],
  resolve: (skip: number, limit: number, filter: BHFilter) => (gqlFields: BaseQLSelectionSet<WorkshopSet>) => {
    if (!filter.institutionID) return of([]);

    const aggregation = [
      { $match: filter.getMongoFilter() },
      { $sort: withIdTiebreaker(filter.getMongoOptions().sort) },
      { $skip: skip },
      { $limit: limit },
      { $project: gqlFields },
    ];

    const getCollection = Promise.resolve(mongo().collection<WorkshopSet>(WorkshopSet._collectionName));
    return watchEntireCollection(WorkshopSet._collectionName).pipe(
      switchMap(() => getCollection),
      switchMap(c => c.aggregate(aggregation, { collation: { locale: 'de' } }).toArray()),
      map(result => result as WorkshopSet[]),
    );
  },
});

export const saveWorkshopSet = registerMutation({
  operationName: 'saveWorkshopSet',
  params: [
    { name: 'workshopSet', type: () => WorkshopSet },
    { name: 'workshopIDs', type: () => [String] },
  ],
  type: () => WorkshopSet,
  middlewares: [
    checkAuthorizedMiddleware(({ user, params }) =>
      user.hasPermission(Permission.EDIT_ADMIN_INSTITUTION_WORKSHOPS, params.workshopSet.institutionID),
    ),
  ],
  resolve: (workshopSet: WorkshopSet, workshopIDs: string[]) => async (gqlFields: BaseQLSelectionSet<WorkshopSet>) => {
    if (!context.serverContext) {
      const currentUser = await getCurrentUserServerOrFail();
      if (workshopSet._id) {
        const storedWorkshopSet = await mongo()
          .collection<WorkshopSet>(WorkshopSet._collectionName)
          .findOne({ _id: workshopSet._id }, { projection: { institutionID: 1 } });
        if (
          storedWorkshopSet &&
          !currentUser.hasPermission(Permission.EDIT_ADMIN_INSTITUTION_WORKSHOPS, storedWorkshopSet.institutionID)
        )
          throw new Error('not authorized');
      }

      const distinctWorkshopIDs = [...new Set(workshopIDs)];
      const referencedWorkshops = await mongo()
        .collection<Workshop>(Workshop._collectionName)
        .find({ _id: { $in: distinctWorkshopIDs } }, { projection: { institutionID: 1 } })
        .toArray();
      if (
        referencedWorkshops.length !== distinctWorkshopIDs.length ||
        referencedWorkshops.some(workshop => workshop.institutionID !== workshopSet.institutionID)
      )
        throw new Error('Workshop set may only reference workshops from its institution');
    }

    if (workshopSet._id) await workshopSet.save();
    else {
      const workshopSetID = await workshopSet.create();
      workshopSet._id = workshopSetID;
    }

    // remove affiliation from workshops not in the set
    const workshopCollection = mongo().collection<Workshop>(Workshop._collectionName);
    await workshopCollection.updateMany(
      {
        _id: { $nin: workshopIDs },
        'affiliation.affiliation': workshopSet._class,
        'affiliation.affiliationID': workshopSet._id,
      },
      { $set: { affiliation: null } },
    );

    // add affiliation to workshops in the set
    const prdCol = mongo().collection<Product>(Product._collectionName);
    for (const workshopID of workshopIDs) {
      const affiliation = new WorkshopAffiliation({ affiliation: workshopSet._class, affiliationID: workshopSet._id });

      prdCol.updateOne({ _id: workshopID }, { $set: { affiliation: affiliation.toObject() } });
    }

    return workshopSet;
  },
});

export const subscribeAllActiveWorkshopSets = registerSubscription({
  operationName: 'subscribeAllActiveWorkshopSets',
  type: () => [WorkshopSet],
  params: [{ name: 'filter', type: () => ProductFilter }],
  resolve: (filter: Maybe<ProductFilter>) => {
    return <SelSet extends BaseQLSelectionSet<WorkshopSet>>(gqlFields: SelSet) => {
      if (!filter) return of([]);
      const institutionFilter = filter.institutionIDs ? { institutionID: { $in: filter.institutionIDs } } : {};
      const timeframeFilter = filter.timeframeIDs ? { timeframeID: { $in: filter.timeframeIDs } } : {};
      const categoryFilter = filter.categoryIDs ? { categoryID: { $in: filter.categoryIDs } } : {};

      const aggregation = [
        { $match: { $and: [institutionFilter, timeframeFilter, categoryFilter] } },
        // TODO: lookup workshops
        // { $match: filter.getMongoFilter() ?? {} },
        // { $sort: filter?.getMongoOptions().sort ?? {} },
      ];

      return watchEntireCollection(WorkshopSet._collectionName).pipe(
        switchMap(async () => mongo().collection<WorkshopSet>(WorkshopSet._collectionName)),
        switchMap(c => c.aggregate(aggregation, { collation: { locale: 'de' } }).toArray()),
        map(result => result as WorkshopSet[]),
      );
    };
  },
});

export const generateWorkshopSetCertificate = registerMutation({
  operationName: 'generateWorkshopSetCertificate',
  type: () => String,
  params: [
    { name: 'workshopSetID', type: () => String },
    { name: 'userID', type: () => String },
  ],
  resolve: (workshopSetID: string, userID: string) => async () => {
    const workshopSet = await getWorkshopSetByID(workshopSetID)({
      _id: 1,
      institutionID: 1,
      name: 1,
      translatedDescriptionHTML: selectionSet(() => TextMultipleLangItem, 3),
      attachedCertificate: selectionSet(() => CertificateAttachedProduct, 3),
    });
    if (!workshopSet) throw new Error('No workshopSet found ' + workshopSetID);
    const user = await getCurrentUserServerOrFail();
    const mayGenerate =
      user._id === userID || user.hasPermission(Permission.MANAGE_PARTICIPATION, workshopSet.institutionID);
    if (!mayGenerate) throw new Error('not authorized');
    const attachedCertificate = workshopSet.attachedCertificate;
    if (!attachedCertificate) throw new Error('no certificate attached');
    const certTemplate = await runInContext(serverContext.sessionID, () =>
      getCertificateWorkshopSetByID(attachedCertificate.certificateID)({
        _id: 1,
        name: 1,
        translatedDescriptionHTML: selectionSet(() => TextMultipleLangItem, 3),
        fileID: 1,
      }),
    );
    if (!certTemplate) throw new Error('No certificate found ' + attachedCertificate.certificateID);
    const templateFile = await getAdornisFileByID(certTemplate.fileID)(selectionSet(() => AdornisFile, 2));
    if (!templateFile) throw new Error('No template-file found ' + attachedCertificate.certificateID);

    const workshops = await loadMongoData(Workshop, {
      'affiliation.affiliation': workshopSet._class,
      'affiliation.affiliationID': workshopSet._id,
    });

    const templater = AdornisTemplater.fromFile(templateFile.getFullPath());
    function getDescriptionText(langKey: string) {
      const translations = workshopSet!.translatedDescriptionHTML;
      const item = translations.find(d => d.langKey === langKey);
      const html = item?.text ?? '';
      return buildifyHtmlToReadableText(html);
    }

    function getCertDescriptionText(langKey: string) {
      const translations = attachedCertificate?.descriptionsHTML;
      const item = translations?.find(d => d.langKey === langKey);
      const html = item?.text ?? '';
      return buildifyHtmlToReadableText(html);
    }

    templater.render({
      [WORKSHOP_SET_CERTIFICATE_VARIABLES.userFirstName]: user.firstName,
      [WORKSHOP_SET_CERTIFICATE_VARIABLES.userLastName]: user.lastName,
      [WORKSHOP_SET_CERTIFICATE_VARIABLES.userDateOfBirth]: user.dateOfBirth
        ? format(user.dateOfBirth, 'dd.MM.yyyy')
        : '---',
      [WORKSHOP_SET_CERTIFICATE_VARIABLES.name]: workshopSet.name,
      [WORKSHOP_SET_CERTIFICATE_VARIABLES.descDe]: getDescriptionText('de'),
      [WORKSHOP_SET_CERTIFICATE_VARIABLES.descEn]: getDescriptionText('en'),
      [WORKSHOP_SET_CERTIFICATE_VARIABLES.descCertDe]: getCertDescriptionText('de'),
      [WORKSHOP_SET_CERTIFICATE_VARIABLES.descCertEn]: getCertDescriptionText('en'),
      [WORKSHOP_SET_CERTIFICATE_VARIABLES.createdAt]: attachedCertificate.exportDate
        ? format(attachedCertificate.exportDate, 'dd.MM.yyyy')
        : '---',
      [WORKSHOP_SET_CERTIFICATE_VARIABLES.workshops.variable]: workshops.map(workshop => ({
        [WORKSHOP_SET_CERTIFICATE_VARIABLES.workshops.subVariables.name]: workshop.name,
      })),
    });

    const pdfBuffer = await templater.toPdfBuffer();
    const file = await AdornisFile.createFromBuffer(pdfBuffer, 'global-certificate.pdf');
    return file._id;
  },
});

export const sendWorkshopSetCertificateToParticipatedUsers = registerSubscription({
  operationName: 'sendWorkshopSetCertificateToParticipatedUsers',
  type: () => String,
  params: [{ name: 'workshopSetID', type: () => String }],
  resolve: (workshopSetID: string) => () => {
    const users = (async () => {
      const workshopSet = await getWorkshopSetByID(workshopSetID)({
        institutionID: 1,
        attachedCertificate: { exportDate: 1 },
      });
      if (!workshopSet) throw new Error('No workshopSet found ' + workshopSetID);
      if (!context.serverContext) {
        const currentUser = await getCurrentUserServerOrFail();
        if (!currentUser.hasPermission(Permission.MANAGE_PARTICIPATION, workshopSet.institutionID))
          throw new Error('not authorized');
      }
      if (!workshopSet.attachedCertificate) throw new Error('No certificate attached');
      workshopSet.attachedCertificate.exportDate = new Date();
      workshopSet.save();

      const workshops = await loadMongoData(Workshop, {
        'affiliation.affiliation': workshopSet._class,
        'affiliation.affiliationID': workshopSetID,
      });

      const workshopParticipations = await runInContext(serverContext.sessionID, () =>
        loadMongoData(ParticipationWorkshop, {
          state: ParticipationState.participated,
          productID: { $in: workshops.map(workshop => workshop._id) },
        }),
      );

      const participationsByUser = workshopParticipations.reduce<Record<string, EntityData<ParticipationWorkshop>[]>>(
        (acc, workshopParticipation) => {
          const userID = workshopParticipation.userID;
          if (!acc[userID]) acc[userID] = [];
          acc[userID].push(workshopParticipation);
          return acc;
        },
        {},
      );

      const usersWhichHaveCompletedAllWorkshops = Object.entries(participationsByUser)
        .filter(([userID, participations]) => participations.length === workshops.length)
        .map(([userID]) => userID);

      return usersWhichHaveCompletedAllWorkshops;
    })();

    let progress = 0;
    let maxProgress = 0;
    return from(users).pipe(
      switchMap(usersWhichHaveCompletedAllWorkshops => {
        if (usersWhichHaveCompletedAllWorkshops.length === 0) throw new Error('No users found');
        maxProgress = usersWhichHaveCompletedAllWorkshops.length;
        return from(usersWhichHaveCompletedAllWorkshops);
      }),
      concatMap(async userID => {
        // create certificate
        const certificateFileID = await generateWorkshopSetCertificate(workshopSetID, userID)();
        await notifyWorkshopSetCompletion(workshopSetID, userID, certificateFileID);
        return `${++progress}/${maxProgress}`;
      }),
    );
  },
});

export const getWorkshopSetCerticatesByInstitution = registerSubscription({
  operationName: 'getWorkshopSetCerticatesByInstitution',
  type: () => [CertificateWorkshopSet],
  params: [{ name: 'institutionID', type: () => String }],
  middlewares: [
    checkAuthorizedMiddleware(({ user, params }) =>
      user.hasPermission(Permission.VIEW_ADMIN_INSTITUTION_WORKSHOPS, params.institutionID),
    ),
  ],
  resolve: (institutionID: string) => (gqlFields: BaseQLSelectionSet<CertificateWorkshopSet>) => {
    return watchEntireCollection(CertificateWorkshopSet._collectionName).pipe(
      switchMap(async () => {
        const certificateCollection = mongo().collection(CertificateWorkshopSet._collectionName);
        logger.info({ institutionID, class: CertificateWorkshopSet._class });
        return certificateCollection
          .find({ institutionID, _class: CertificateWorkshopSet._class })
          .toArray() as any as CertificateWorkshopSet[];
      }),
      tap(certificates => {
        logger.info({ certificates }, 'empty or waht ??????????????????????????????');
      }),
    );
  },
});

export function getWorkshopSetCertificatePreviewUrl(fileID: string) {
  return A.absoluteUrl(`/api/workshop-set-certificate-preview/${fileID}`);
}
export function workshopSetCertificatePreview() {
  const router = Router();
  router.use('/api/workshop-set-certificate-preview', async (req, res, next) => {
    const fileID = req.path.split('/')[1];
    if (!fileID) {
      logger.error('No fileID given for certificate preview');
      return res.sendStatus(422);
    }

    const requestContext = await getContextFromRequest(req);
    if (!requestContext?.userID) return res.sendStatus(401);
    const mayPreview = await runInContext(requestContext.sessionID, async () => {
      const currentUser = await getCurrentUserServerOrFail();
      return currentUser.isAdmin;
    });
    if (!mayPreview) return res.sendStatus(403);

    const certificateTemplateFile = await getAdornisFileByID(fileID)(selectionSet(() => AdornisFile, 2));
    if (!certificateTemplateFile) {
      logger.error('Could not find certificateFile for ID ' + fileID);
      return res.sendStatus(422);
    }

    try {
      const templater = AdornisTemplater.fromFile(certificateTemplateFile.getFullPath());
      const workshops = [{ name: 'Workshop 1' }, { name: 'Workshop 2' }];
      templater.render({
        [WORKSHOP_SET_CERTIFICATE_VARIABLES.userFirstName]: 'Max',
        [WORKSHOP_SET_CERTIFICATE_VARIABLES.userLastName]: 'Mustermann',
        [WORKSHOP_SET_CERTIFICATE_VARIABLES.userDateOfBirth]: '01.01.2000',
        [WORKSHOP_SET_CERTIFICATE_VARIABLES.name]: 'Workshop-Reihe-Beispiel-Name',
        [WORKSHOP_SET_CERTIFICATE_VARIABLES.descDe]: 'lorem ipsum',
        [WORKSHOP_SET_CERTIFICATE_VARIABLES.descEn]: 'lorem ipsum',
        [WORKSHOP_SET_CERTIFICATE_VARIABLES.descCertDe]: 'lorem ipsum',
        [WORKSHOP_SET_CERTIFICATE_VARIABLES.descCertEn]: 'lorem ipsum',
        [WORKSHOP_SET_CERTIFICATE_VARIABLES.createdAt]: format(new Date(), 'dd.MM.yyyy'),
        [WORKSHOP_SET_CERTIFICATE_VARIABLES.workshops.variable]: workshops.map(workshop => ({
          [WORKSHOP_SET_CERTIFICATE_VARIABLES.workshops.subVariables.name]: workshop.name,
        })),
      });

      const fileBuffer = await templater.toPdfBuffer();
      res.setHeader('content-type', 'application/pdf');
      return res.end(fileBuffer);
    } catch (e) {
      logger.warn(e);
      return res.status(500).send('could not generate certificate');
    }
  });
  return router;
}

export const getWorkshopSetsByIDs = registerQuery({
  operationName: 'getWorkshopSetsByIDs',
  type: () => [WorkshopSet],
  params: [{ name: 'ids', type: () => [ID] }],
  resolve:
    (ids: string[]) =>
    <SelSet extends BaseQLSelectionSet<WorkshopSet>>(gqlFields: SelSet) =>
      readEntitiesByIDs(WorkshopSet, ids, gqlFields),
});

export const subscribeWorkshopSetsByIDs = registerSubscription({
  operationName: 'subscribeWorkshopSetsByIDs',
  type: () => [WorkshopSet],
  params: [{ name: 'ids', type: () => [ID] }],
  resolve:
    (ids: string[]) =>
    <SelSet extends BaseQLSelectionSet<WorkshopSet>>(gqlFields: SelSet) =>
      watchEntitiesByIDs(WorkshopSet, ids, gqlFields),
});

export const getAllWorkshopSets = registerQuery({
  operationName: 'getAllWorkshopSets',
  type: () => [WorkshopSet],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'excludeIDs', type: () => [ID] },
  ],
  resolve:
    (skip?: number, limit?: number, excludeIDs?: string[]) =>
    <SelSet extends BaseQLSelectionSet<WorkshopSet>>(gqlFields: SelSet) =>
      readAllEntities(WorkshopSet, gqlFields, { skip, limit, excludeIDs }),
});

export const subscribeAllWorkshopSets = registerSubscription({
  operationName: 'subscribeAllWorkshopSets',
  type: () => [WorkshopSet],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'excludeIDs', type: () => [ID] },
  ],
  resolve:
    (skip?: number, limit?: number, excludeIDs?: string[]) =>
    <SelSet extends BaseQLSelectionSet<WorkshopSet>>(gqlFields: SelSet) =>
      watchAllEntities(WorkshopSet, gqlFields, { skip, limit, excludeIDs }),
});

export const getWorkshopSetByID =
  (id: string | null | undefined) =>
  async <SelSet extends BaseQLSelectionSet<WorkshopSet>>(gqlFields: SelSet) => {
    if (!id) return null;
    return (await getWorkshopSetsByIDs([id])(gqlFields))[0] ?? null;
  };

export const subscribeWorkshopSetByID =
  (id: string | null | undefined) =>
  <SelSet extends BaseQLSelectionSet<WorkshopSet>>(gqlFields: SelSet) =>
    id ? subscribeWorkshopSetsByIDs([id])(gqlFields).pipe(map(entities => entities[0] ?? null)) : of(null);
